import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Row,
  Space,
  Spin,
  Tag,
  theme,
  Typography,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentStudyYear, useStudyYears } from '../../hooks/useStudyYear';
import { CalendarLanguages, CalendarYearType } from '../../Types/interfaces';
import type { NewStudyYear, StudyYearCalendar } from '../../Types/interfaces';
import { useRomanianPublicHolidays } from '../../hooks/useRomanianPublicHolidays';
import { formatDate } from '../../Utils/date';
import type { PeriodBoundary } from './MonthCalendar';
import {
  getDayStatusStyle,
  getHolidayStyle,
  getSecondSemesterWorkingStyle,
  getPeriodBoundaryStyle,
  getTodayStyle,
  MonthCalendar,
} from './MonthCalendar';
import type {
  CalendarDays,
  DayStatus,
  Semester,
  TeachingPeriod,
} from './calendarUtils';
import {
  buildAcademicCalendar,
  fromDateKey,
  getDefaultDayStatus,
  getDefaultSemesterPeriod,
  getDefaultTeachingPeriod,
  getMarkedDays,
  getStudyYearDayStatus,
  getStudyYearLastDay,
  mergeSemesters,
  OCTOBER,
  SEPTEMBER,
  toCalendarDays,
  toDateKey,
  toStudyYearCalendar,
} from './calendarUtils';

// The start / end of which semester a click sets
type SemesterBoundary = 'firstSemester' | 'secondSemester';

// Sets the start / end of the study year, the same in all four calendars
type StudyYearBoundary = 'studyYear';

// What a click on a day does: sets the start / end of the study year or of a semester,
// or marks the day as one of the statuses
type MarkOption = SemesterBoundary | StudyYearBoundary | DayStatus;

// The options of the 'Mark as' row, which is also the legend of the colours
const MARK_OPTIONS: Array<MarkOption> = [
  'firstSemester',
  'secondSemester',
  'working',
  'free',
  'exam',
  'retake',
  'outside',
  'studyYear',
];

// In the setup the start / end of the study year is chosen in its own step, before the semesters
const STUDY_YEAR_MARK_OPTIONS: Array<MarkOption> = ['studyYear'];

const isSemesterBoundary = (option?: MarkOption): option is SemesterBoundary =>
  option === 'firstSemester' || option === 'secondSemester';

const isStudyYearBoundary = (
  option?: MarkOption
): option is StudyYearBoundary => option === 'studyYear';

const isDayStatusOption = (option?: MarkOption): option is DayStatus =>
  option !== undefined &&
  !isSemesterBoundary(option) &&
  !isStudyYearBoundary(option);

// These are marked for a whole interval: a click on its first day, then on its last day
const RANGE_STATUSES: Array<MarkOption> = ['exam', 'retake'];

// What the rector changed in a semester: its period, if not the default,
// and the days switched from their default status, keyed by 'YYYY-MM-DD'
type CalendarChanges = {
  period?: TeachingPeriod;
  overrides: Record<string, DayStatus>;
};

// What the rector changed in the semesters of a calendar: of a study line group and kind of years
type LineChanges = Partial<Record<Semester, CalendarChanges>>;

// The first semester starts with the study year ('YYYY-MM-DD') in every calendar,
// only its end is set per calendar
const getLineSemesterPeriod = (
  studyYearStart: number,
  lineChanges: LineChanges | undefined,
  semester: Semester,
  studyYearStartDate: string
): TeachingPeriod => {
  const period =
    lineChanges?.[semester]?.period ??
    getDefaultSemesterPeriod(studyYearStart, semester);
  return semester === 'first'
    ? { ...period, start: studyYearStartDate }
    : period;
};

const buildSemesterCalendar = (
  studyYearStart: number,
  lineChanges: LineChanges | undefined,
  semester: Semester,
  studyYearStartDate: string,
  holidayDateKeys: ReadonlySet<string>
): CalendarDays =>
  buildAcademicCalendar(
    studyYearStart,
    getLineSemesterPeriod(
      studyYearStart,
      lineChanges,
      semester,
      studyYearStartDate
    ),
    lineChanges?.[semester]?.overrides ?? {},
    holidayDateKeys
  );

// The four calendars of a study year, in the order of their tabs
const CALENDAR_TABS: Array<{
  languages: CalendarLanguages;
  yearType: CalendarYearType;
}> = [
  {
    languages: CalendarLanguages.RomanianEnglish,
    yearType: CalendarYearType.NonFinal,
  },
  {
    languages: CalendarLanguages.RomanianEnglish,
    yearType: CalendarYearType.Final,
  },
  {
    languages: CalendarLanguages.HungarianGerman,
    yearType: CalendarYearType.NonFinal,
  },
  {
    languages: CalendarLanguages.HungarianGerman,
    yearType: CalendarYearType.Final,
  },
];

// The study year starts in October, so before October we are still in the previous one
const getStudyYearStart = (date: Date): number =>
  date.getMonth() >= OCTOBER ? date.getFullYear() : date.getFullYear() - 1;

type AcademicCalendarComponentProps = {
  // In the study year setup the calendar of the new study year is only set here,
  // it is given to the setup (undefined for a saved study year) instead of being started here
  onStudyYearChange?: (studyYear: NewStudyYear | undefined) => void;
  // On the study years page the study year (its start year, e.g. 2025) is chosen by the page,
  // then the calendar doesn't show its own title and study year selector
  studyYearStart?: number;
  // In the study year setup the start / end of the study year is chosen in a step before the semesters.
  // Both steps show this calendar, so they share the same changes
  setupStep?: 'studyYear' | 'semesters';
  // In the setup: false while a semester doesn't fit into the study year, then it can't be started
  onValidChange?: (isValid: boolean) => void;
};

export const AcademicCalendarComponent = ({
  onStudyYearChange,
  studyYearStart: chosenYearStart,
  setupStep = 'semesters',
  onValidChange,
}: AcademicCalendarComponentProps): React.ReactElement => {
  const isSetup = onStudyYearChange !== undefined;
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { data: currentStudyYear, isLoading } = useCurrentStudyYear();
  const { data: studyYears, isLoading: isLoadingStudyYears } = useStudyYears();
  const [calendarLanguages, setCalendarLanguages] = useState<CalendarLanguages>(
    CalendarLanguages.RomanianEnglish
  );

  // The backend's study year may start a bit before October (e.g. in September),
  // so take its start year as is; fall back to today's date if it isn't set
  const currentYearStart = useMemo(
    () =>
      currentStudyYear
        ? new Date(currentStudyYear.startDate).getFullYear()
        : getStudyYearStart(new Date()),
    [currentStudyYear]
  );

  const createdYearStarts = useMemo(
    () =>
      new Set(
        (studyYears ?? []).map((studyYear) =>
          new Date(studyYear.startDate).getFullYear()
        )
      ),
    [studyYears]
  );

  const nextYearStart = Math.max(currentYearStart, ...createdYearStarts) + 1;

  // In the setup the first study year which isn't created yet is shown:
  // the current one before the first study year, otherwise the one after the latest
  const defaultYearStart =
    isSetup && createdYearStarts.size > 0 ? nextYearStart : currentYearStart;

  const studyYearStart = chosenYearStart ?? defaultYearStart;

  // September of the start year → October of the next year,
  // so the rector can also start the teaching period in September
  // and see where the next study year begins
  const months = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => {
        const date = new Date(studyYearStart, SEPTEMBER + i, 1);
        return { year: date.getFullYear(), month: date.getMonth() };
      }),
    [studyYearStart]
  );

  const {
    holidays,
    isLoading: isLoadingHolidays,
    isError: isHolidaysError,
  } = useRomanianPublicHolidays([studyYearStart, studyYearStart + 1]);

  // Shown on the screen in the user's language; only the dates are stored in the study year.
  // Some dates have two holidays (e.g. Easter and 1 May), so their names are joined
  const holidayNames = useMemo(() => {
    const names = new Map<string, string>();
    holidays.forEach((holiday) => {
      const name = i18n.language === 'ro' ? holiday.localName : holiday.name;
      const existing = names.get(holiday.date);
      names.set(holiday.date, existing ? `${existing}, ${name}` : name);
    });
    return names;
  }, [holidays, i18n.language]);

  const holidayDateKeys = useMemo(
    () => new Set(holidayNames.keys()),
    [holidayNames]
  );

  const getHolidayName = useCallback(
    (date: Date) => holidayNames.get(toDateKey(date)),
    [holidayNames]
  );

  const [yearType, setYearType] = useState<CalendarYearType>(
    CalendarYearType.NonFinal
  );

  // Nothing is selected until an option is chosen, and again after a start / end
  // or an interval is set, so a change needs the option chosen again
  const [markOption, setMarkOption] = useState<MarkOption | undefined>(
    setupStep === 'studyYear' ? 'studyYear' : undefined
  );
  const isRangeStatus =
    markOption !== undefined && RANGE_STATUSES.includes(markOption);
  // The first day ('YYYY-MM-DD') of the interval being marked, until its last day is clicked
  const [rangeStart, setRangeStart] = useState<string>();
  // When setting the start / end of a semester the clicks alternate: first the start, then the end
  const [nextBoundary, setNextBoundary] = useState<'start' | 'end'>('start');
  // The day under the mouse ('YYYY-MM-DD'), to preview the period
  // or the interval while its end is chosen
  const [hoveredKey, setHoveredKey] = useState<string>();

  // Clears a half-chosen start / end or interval
  const cancelSelection = () => {
    setNextBoundary('start');
    setRangeStart(undefined);
  };

  // Each setup step has its own options: the start / end of the study year, then the semesters and days,
  // so they are reset when the step changes
  const [shownSetupStep, setShownSetupStep] = useState(setupStep);
  if (shownSetupStep !== setupStep) {
    setShownSetupStep(setupStep);
    setMarkOption(setupStep === 'studyYear' ? 'studyYear' : undefined);
    cancelSelection();
  }

  // What the rector changed, per study year, calendar and semester; everything else is the default.
  // The study lines by teaching language have their own calendars: Romanian and English,
  // or Hungarian and German, each for the non-final and the final years, so there are four calendars,
  // and both semesters of each can be changed on their own
  const [changes, setChanges] = useState<
    Record<
      number,
      Partial<
        Record<
          CalendarLanguages,
          Partial<Record<CalendarYearType, LineChanges>>
        >
      >
    >
  >({});

  const lineChanges = changes[studyYearStart]?.[calendarLanguages]?.[yearType];

  // The start / end of the study year ('YYYY-MM-DD') the rector chose, per study year;
  // they are the same in all four calendars
  const [studyYearBounds, setStudyYearBounds] = useState<
    Partial<Record<number, Partial<TeachingPeriod>>>
  >({});

  // The start / end of the new study year: by default 1 October and the day before it a year later
  const yearBounds = useMemo((): TeachingPeriod => {
    const start =
      studyYearBounds[studyYearStart]?.start ??
      getDefaultSemesterPeriod(studyYearStart, 'first').start;
    return {
      start,
      end: studyYearBounds[studyYearStart]?.end ?? getStudyYearLastDay(start),
    };
  }, [studyYearBounds, studyYearStart]);

  // The selected study year as it is saved in the database, if it was already created.
  // A saved study year is shown as it is and can't be changed
  const savedStudyYear = useMemo(
    () =>
      studyYears?.find(
        (studyYear) =>
          new Date(studyYear.startDate).getFullYear() === studyYearStart
      ),
    [studyYears, studyYearStart]
  );
  const isSaved = savedStudyYear !== undefined;

  // The saved calendar of the chosen study line and years
  const savedCalendar = useMemo(
    (): StudyYearCalendar | undefined =>
      savedStudyYear?.calendars.find(
        (calendar) =>
          calendar.languages === calendarLanguages &&
          calendar.yearType === yearType
      ),
    [calendarLanguages, savedStudyYear, yearType]
  );

  // A new study year and a saved calendar have two semesters, a study year
  // saved before the calendars has only its start / end
  const showsSemesters = !isSaved || savedCalendar !== undefined;

  const savedPeriod = useMemo(
    (): TeachingPeriod | undefined =>
      savedStudyYear && {
        start: savedStudyYear.startDate,
        end:
          savedStudyYear.endDate ??
          getDefaultTeachingPeriod(studyYearStart).end,
      },
    [savedStudyYear, studyYearStart]
  );

  // A study year saved with its own end has it after the end of every second semester. Before that
  // its end was the end of the latest second semester, which is already shown as the end of a semester
  const savedStudyYearEnd = useMemo(() => {
    const end = savedStudyYear?.endDate?.slice(0, 10);
    const secondEnds = (savedStudyYear?.calendars ?? [])
      .map((calendar) => calendar.secondSemesterEnd)
      .sort();
    const latestSecondEnd = secondEnds[secondEnds.length - 1];
    return end && latestSecondEnd && end > latestSecondEnd ? end : undefined;
  }, [savedStudyYear]);

  // The days of the study year: a new one lasts from its chosen start to its chosen end, a saved one
  // until its end, or the day before the next study year starts. The days of another study year are shown faded
  const studyYearDays = useMemo((): TeachingPeriod => {
    if (!savedStudyYear) {
      return yearBounds;
    }
    const start = savedStudyYear.startDate.slice(0, 10);
    const nextStart = (studyYears ?? [])
      .map((studyYear) => studyYear.startDate.slice(0, 10))
      .filter((studyYearStartDate) => studyYearStartDate > start)
      .sort()[0];
    return {
      start,
      end: savedStudyYearEnd ?? getStudyYearLastDay(start, nextStart),
    };
  }, [savedStudyYear, savedStudyYearEnd, studyYears, yearBounds]);

  const isDayInStudyYear = useCallback(
    (date: Date): boolean => {
      const key = toDateKey(date);
      return key >= studyYearDays.start && key <= studyYearDays.end;
    },
    [studyYearDays]
  );

  const getSemesterPeriod = useCallback(
    (semester: Semester): TeachingPeriod =>
      getLineSemesterPeriod(
        studyYearStart,
        lineChanges,
        semester,
        yearBounds.start
      ),
    [lineChanges, studyYearStart, yearBounds.start]
  );

  const firstPeriod = useMemo(
    () => getSemesterPeriod('first'),
    [getSemesterPeriod]
  );
  const secondPeriod = useMemo(
    () => getSemesterPeriod('second'),
    [getSemesterPeriod]
  );

  // The semester of a day ('YYYY-MM-DD'), if it is in one; the first one wins if they overlap
  const getSemesterOfDay = useCallback(
    (key: string): Semester | undefined => {
      if (key >= firstPeriod.start && key <= firstPeriod.end) {
        return 'first';
      }
      if (key >= secondPeriod.start && key <= secondPeriod.end) {
        return 'second';
      }
      return undefined;
    },
    [firstPeriod, secondPeriod]
  );

  // The second semester of the calendar shown, whose teaching days are green: the saved one,
  // or the one being set. While the study year's start / end is chosen there are no semesters
  const isDayInSecondSemester = useCallback(
    (date: Date): boolean => {
      if (setupStep === 'studyYear') {
        return false;
      }
      const second = savedCalendar
        ? {
            start: savedCalendar.secondSemesterStart,
            end: savedCalendar.secondSemesterEnd,
          }
        : isSaved
          ? undefined
          : secondPeriod;
      const key = toDateKey(date);
      return !!second && key >= second.start && key <= second.end;
    },
    [isSaved, savedCalendar, secondPeriod, setupStep]
  );

  const buildSemester = useCallback(
    (semester: Semester) =>
      buildSemesterCalendar(
        studyYearStart,
        lineChanges,
        semester,
        yearBounds.start,
        holidayDateKeys
      ),
    [holidayDateKeys, lineChanges, studyYearStart, yearBounds.start]
  );

  // The calendar shown: the saved one of the chosen study line and years, or the first semester
  // and the second semester of the chosen study line and years, built from the rector's changes
  const calendar = useMemo((): CalendarDays => {
    if (savedCalendar) {
      return toCalendarDays(savedCalendar);
    }
    if (savedPeriod) {
      // Study years saved before the calendars have none,
      // so they are shown with the default rules inside their saved period
      return buildAcademicCalendar(
        studyYearStart,
        savedPeriod,
        {},
        holidayDateKeys
      );
    }
    return mergeSemesters(buildSemester('first'), buildSemester('second'));
  }, [
    buildSemester,
    holidayDateKeys,
    savedCalendar,
    savedPeriod,
    studyYearStart,
  ]);

  // The new study year with all four calendars, whichever is shown
  const studyYearToSave = useMemo((): NewStudyYear => {
    const calendars = Object.values(CalendarLanguages).flatMap((languages) =>
      Object.values(CalendarYearType).map((type) => {
        const build = (semester: Semester) =>
          buildSemesterCalendar(
            studyYearStart,
            changes[studyYearStart]?.[languages]?.[type],
            semester,
            yearBounds.start,
            holidayDateKeys
          );
        return toStudyYearCalendar(
          languages,
          type,
          build('first'),
          build('second')
        );
      })
    );

    // The study year lasts from its chosen start, the start of the first semester in every calendar,
    // to its chosen end, after the second semester of every calendar
    return {
      name: `${studyYearStart}/${studyYearStart + 1}`,
      startDate: yearBounds.start,
      endDate: yearBounds.end,
      calendars,
    };
  }, [changes, holidayDateKeys, studyYearStart, yearBounds]);

  // In the setup the calendar is given to the setup, which sends it together with the departments
  useEffect(() => {
    onStudyYearChange?.(isSaved ? undefined : studyYearToSave);
  }, [isSaved, onStudyYearChange, studyYearToSave]);

  // The semesters of every calendar have to be inside the study year. Any day can still be chosen,
  // e.g. after going back to the study year step, but then a warning lists what doesn't fit
  const calendarWarnings = useMemo(
    () =>
      studyYearToSave.calendars.flatMap((c) => {
        const calendarLabel = `${
          c.languages === CalendarLanguages.RomanianEnglish
            ? t('academicCalendar.languagesRoEn')
            : t('academicCalendar.languagesHuDe')
        } · ${
          c.yearType === CalendarYearType.Final
            ? t('academicCalendar.yearTypeFinal')
            : t('academicCalendar.yearTypeNonFinal')
        }`;
        const warnings: Array<string> = [];
        if (c.firstSemesterEnd < yearBounds.start) {
          warnings.push(
            t('academicCalendar.firstSemesterBeforeStudyYearStart', {
              calendar: calendarLabel,
              semesterEnd: formatDate(c.firstSemesterEnd),
              studyYearStart: formatDate(yearBounds.start),
            })
          );
        }
        if (c.secondSemesterEnd > yearBounds.end) {
          warnings.push(
            t('academicCalendar.secondSemesterAfterStudyYearEnd', {
              calendar: calendarLabel,
              semesterEnd: formatDate(c.secondSemesterEnd),
              studyYearEnd: formatDate(yearBounds.end),
            })
          );
        }
        return warnings;
      }),
    [studyYearToSave, t, yearBounds]
  );

  // The setup can't start a study year whose semesters don't fit into it
  useEffect(() => {
    onValidChange?.(isSaved || calendarWarnings.length === 0);
  }, [calendarWarnings, isSaved, onValidChange]);

  // The days are shown from the calendar object, so the UI shows exactly what is built
  const markedDays = useMemo(() => getMarkedDays(calendar), [calendar]);

  // While its start / end is chosen, the study year is shown without the semesters:
  // the weekdays inside it are working days, the weekends and public holidays free days
  const getDayStatus = useCallback(
    (date: Date): DayStatus =>
      setupStep === 'studyYear'
        ? getDefaultDayStatus(date, studyYearDays, holidayDateKeys)
        : getStudyYearDayStatus(calendar, markedDays, toDateKey(date)),
    [calendar, holidayDateKeys, markedDays, setupStep, studyYearDays]
  );

  // Changes the semesters of the chosen calendar: of the chosen study lines and years
  const updateSemesterChanges = useCallback(
    (update: (previous: LineChanges) => LineChanges) =>
      setChanges((previous) => ({
        ...previous,
        [studyYearStart]: {
          ...previous[studyYearStart],
          [calendarLanguages]: {
            ...previous[studyYearStart]?.[calendarLanguages],
            [yearType]: update(
              previous[studyYearStart]?.[calendarLanguages]?.[yearType] ?? {}
            ),
          },
        },
      })),
    [calendarLanguages, studyYearStart, yearType]
  );

  // Sets the status of the days, each in its own semester;
  // switching back to the default doesn't need to be stored
  const setDayStatuses = useCallback(
    (
      days: Array<{
        key: string;
        semester: Semester;
        status: DayStatus;
        defaultStatus: DayStatus;
      }>
    ) =>
      updateSemesterChanges((previous) => {
        const semesters = { ...previous };
        days.forEach(({ key, semester, status, defaultStatus }) => {
          const semesterChanges = semesters[semester] ?? { overrides: {} };
          const overrides = { ...semesterChanges.overrides };
          if (status === defaultStatus) {
            delete overrides[key];
          } else {
            overrides[key] = status;
          }
          semesters[semester] = { ...semesterChanges, overrides };
        });
        return semesters;
      }),
    [updateSemesterChanges]
  );

  const toggleDay = useCallback(
    (date: Date) => {
      const key = toDateKey(date);
      const semester = getSemesterOfDay(key);
      if (!isDayStatusOption(markOption) || !semester) {
        return;
      }
      const defaultStatus = getDefaultDayStatus(
        date,
        getSemesterPeriod(semester),
        holidayDateKeys
      );
      // A second click with the same option sets the day back to its default
      const status =
        getDayStatus(date) === markOption ? defaultStatus : markOption;
      setDayStatuses([{ key, semester, status, defaultStatus }]);
    },
    [
      getDayStatus,
      getSemesterOfDay,
      getSemesterPeriod,
      holidayDateKeys,
      markOption,
      setDayStatuses,
    ]
  );

  // The first click keeps the first day of the interval, the second marks every day
  // up to the clicked one, in both semesters if it spans them. Exam and retake sessions
  // don't take the weekends and public holidays, those stay free. If the whole interval
  // is already marked so, it is set back to its default, like a second click on a single day
  const markRange = useCallback(
    (date: Date) => {
      if (!isDayStatusOption(markOption)) {
        return;
      }
      const key = toDateKey(date);
      if (!rangeStart) {
        setRangeStart(key);
        return;
      }
      const [from, to] =
        rangeStart <= key ? [rangeStart, key] : [key, rangeStart];
      setRangeStart(undefined);
      // The interval is marked, another one needs the option chosen again
      setMarkOption(undefined);

      const days: Array<{
        date: Date;
        semester: Semester;
        defaultStatus: DayStatus;
      }> = [];
      const end = fromDateKey(to);
      for (
        const day = fromDateKey(from);
        day <= end;
        day.setDate(day.getDate() + 1)
      ) {
        const semester = getSemesterOfDay(toDateKey(day));
        if (!semester) {
          continue;
        }
        const defaultStatus = getDefaultDayStatus(
          day,
          getSemesterPeriod(semester),
          holidayDateKeys
        );
        if (defaultStatus !== 'free') {
          days.push({ date: new Date(day), semester, defaultStatus });
        }
      }
      const isAlreadyMarked = days.every(
        ({ date: day }) => getDayStatus(day) === markOption
      );

      setDayStatuses(
        days.map(({ date: day, semester, defaultStatus }) => ({
          key: toDateKey(day),
          semester,
          status: isAlreadyMarked ? defaultStatus : markOption,
          defaultStatus,
        }))
      );
    },
    [
      getDayStatus,
      getSemesterOfDay,
      getSemesterPeriod,
      holidayDateKeys,
      markOption,
      rangeStart,
      setDayStatuses,
    ]
  );

  // The semester whose start / end the selected option sets
  const boundarySemester: Semester =
    markOption === 'firstSemester' ? 'first' : 'second';
  const boundaryPeriod =
    boundarySemester === 'first' ? firstPeriod : secondPeriod;

  // The first semester starts with the study year, so only its end is set here
  const setsOnlyEnd = boundarySemester === 'first';

  const setBoundary = useCallback(
    (date: Date) => {
      const key = toDateKey(date);
      const [start, end] =
        nextBoundary === 'start' && !setsOnlyEnd
          ? [key, boundaryPeriod.end]
          : [boundaryPeriod.start, key];
      // If the end ends up before the start, the two are swapped
      updateSemesterChanges((previous) => ({
        ...previous,
        [boundarySemester]: {
          overrides: {},
          ...previous[boundarySemester],
          period: start <= end ? { start, end } : { start: end, end: start },
        },
      }));
      // Once the end is set, a change needs the option chosen again
      if (setsOnlyEnd || nextBoundary === 'end') {
        setNextBoundary('start');
        setMarkOption(undefined);
      } else {
        setNextBoundary('end');
      }
    },
    [
      boundaryPeriod,
      boundarySemester,
      nextBoundary,
      setsOnlyEnd,
      updateSemesterChanges,
    ]
  );

  // The start / end of the study year is the same in all four calendars;
  // like the semesters, the clicks alternate: first its start, then its end.
  // Any day can be chosen, if the end ends up before the start, the two are swapped
  const setStudyYearBoundary = useCallback(
    (date: Date) => {
      const key = toDateKey(date);
      setStudyYearBounds((previous) => {
        const bounds = { ...previous[studyYearStart], [nextBoundary]: key };
        return {
          ...previous,
          [studyYearStart]:
            bounds.start && bounds.end && bounds.end < bounds.start
              ? { start: bounds.end, end: bounds.start }
              : bounds,
        };
      });
      // Once the end is set, a change needs the option chosen again
      if (nextBoundary === 'end') {
        setNextBoundary('start');
        setMarkOption(undefined);
      } else {
        setNextBoundary('end');
      }
    },
    [nextBoundary, studyYearStart]
  );

  const getPeriodBoundary = useCallback(
    (date: Date): PeriodBoundary | undefined => {
      const key = toDateKey(date);
      const [first, second] = savedCalendar
        ? [
            {
              start: savedCalendar.firstSemesterStart,
              end: savedCalendar.firstSemesterEnd,
            },
            {
              start: savedCalendar.secondSemesterStart,
              end: savedCalendar.secondSemesterEnd,
            },
          ]
        : [firstPeriod, secondPeriod];
      // The end of the study year is shown in every calendar, its start is the start of the first semester
      const studyYearEnd = isSaved ? savedStudyYearEnd : yearBounds.end;
      const studyYearEndBoundary: Array<
        [string, string, PeriodBoundary['semester']]
      > = studyYearEnd ? [[studyYearEnd, 'studyYearEnd', 'studyYear']] : [];
      const boundaries: Array<[string, string, PeriodBoundary['semester']]> =
        setupStep === 'studyYear'
          ? [
              [studyYearDays.start, 'studyYearStart', 'studyYear'],
              ...studyYearEndBoundary,
            ]
          : savedPeriod && !savedCalendar
            ? [
                [savedPeriod.start, 'studyYearStart', 'first'],
                [savedPeriod.end, 'studyYearEnd', 'first'],
              ]
            : [
                [first.start, 'firstSemesterStart', 'first'],
                [first.end, 'firstSemesterEnd', 'first'],
                [second.start, 'secondSemesterStart', 'second'],
                [second.end, 'secondSemesterEnd', 'second'],
                ...studyYearEndBoundary,
              ];
      const boundary = boundaries.find(([boundaryKey]) => boundaryKey === key);
      return (
        boundary && {
          label: t(`academicCalendar.${boundary[1]}`),
          semester: boundary[2],
        }
      );
    },
    [
      firstPeriod,
      isSaved,
      savedCalendar,
      savedPeriod,
      savedStudyYearEnd,
      secondPeriod,
      setupStep,
      studyYearDays.start,
      t,
      yearBounds.end,
    ]
  );

  // Only the days of the semesters can be marked, also the ones marked as outside of them.
  // The start / end of the study year can be any day, the semesters are inside the study year
  // and don't overlap: the first semester ends before the second one starts
  const canClickDay = useCallback(
    (date: Date) => {
      const key = toDateKey(date);
      if (isStudyYearBoundary(markOption)) {
        return true;
      }
      if (isSemesterBoundary(markOption)) {
        const isInStudyYear = key >= yearBounds.start && key < yearBounds.end;
        return markOption === 'firstSemester'
          ? isInStudyYear && key < secondPeriod.start
          : isInStudyYear && key > firstPeriod.end;
      }
      return getSemesterOfDay(key) !== undefined;
    },
    [firstPeriod, getSemesterOfDay, markOption, secondPeriod, yearBounds]
  );

  // After the first day of a semester, of the study year or of an interval is clicked, the days from it
  // to the one under the mouse are shown as a grey ghost, as they would be marked
  // if that day was clicked
  const isInRangePreview = useCallback(
    (date: Date) => {
      const previewStart = isStudyYearBoundary(markOption)
        ? nextBoundary === 'end'
          ? yearBounds.start
          : undefined
        : isSemesterBoundary(markOption)
          ? nextBoundary === 'end' || setsOnlyEnd
            ? boundaryPeriod.start
            : undefined
          : rangeStart;
      if (!previewStart) {
        return false;
      }
      const previewEnd = hoveredKey ?? previewStart;
      const key = toDateKey(date);
      const [from, to] =
        previewStart <= previewEnd
          ? [previewStart, previewEnd]
          : [previewEnd, previewStart];
      return key >= from && key <= to;
    },
    [
      boundaryPeriod,
      hoveredKey,
      markOption,
      nextBoundary,
      rangeStart,
      setsOnlyEnd,
      yearBounds.start,
    ]
  );

  // The reset is about what is shown: the start / end of the study year in its step,
  // otherwise both semesters of the chosen calendar
  const hasChanges =
    setupStep === 'studyYear'
      ? studyYearBounds[studyYearStart] !== undefined
      : lineChanges?.first !== undefined || lineChanges?.second !== undefined;

  const resetShown = () => {
    if (setupStep === 'studyYear') {
      setStudyYearBounds((previous) => ({
        ...previous,
        [studyYearStart]: undefined,
      }));
    } else {
      updateSemesterChanges(() => ({}));
    }
    cancelSelection();
  };

  const getMarkOptionLabel = (option: MarkOption) => {
    if (option === 'firstSemester') {
      // In the setup the first semester starts with the study year, only its end is set
      return !showsSemesters
        ? t('academicCalendar.studyYearBoundary')
        : isSetup
          ? t('academicCalendar.firstSemesterEnd')
          : t('academicCalendar.firstSemesterBoundary');
    }
    if (option === 'secondSemester') {
      return t('academicCalendar.secondSemesterBoundary');
    }
    // After its step only the end of the study year is shown apart, its start is the start of the first semester
    if (option === 'studyYear') {
      return setupStep === 'studyYear'
        ? t('academicCalendar.studyYearBoundary')
        : t('academicCalendar.studyYearEnd');
    }
    return t(`academicCalendar.${option}`);
  };

  // The colour of an option with its name
  // An option of the 'Mark as' row: a small chip, outlined when it is selected
  const getMarkChipStyle = (
    isSelected: boolean,
    isClickable: boolean
  ): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: token.borderRadius,
    border: `1px solid ${isSelected ? token.colorPrimary : 'transparent'}`,
    background: isSelected ? token.colorPrimaryBg : token.colorFillTertiary,
    color: isSelected ? token.colorPrimary : token.colorText,
    font: 'inherit',
    fontSize: 13,
    cursor: isClickable ? 'pointer' : 'default',
  });

  // A colour which can't be marked is only explained, so it isn't drawn as a chip
  const legendOnlyStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 4px',
    fontSize: 13,
    color: token.colorText,
  };

  const renderSwatch = (style: React.CSSProperties): React.ReactElement => (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: token.borderRadiusSM,
        border: `1px solid ${token.colorBorder}`,
        verticalAlign: 'middle',
        ...style,
      }}
    />
  );

  // A teaching day has two colours when there are two semesters: blue in the first, green in the second
  const renderMarkOption = (option: MarkOption): React.ReactElement => (
    <Space size={6}>
      {option === 'working' && showsSemesters ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          {renderSwatch(getDayStatusStyle('working', token))}
          <span aria-hidden="true">/</span>
          {renderSwatch(getSecondSemesterWorkingStyle(token))}
        </span>
      ) : (
        renderSwatch(
          isDayStatusOption(option)
            ? getDayStatusStyle(option, token)
            : getPeriodBoundaryStyle(
                token,
                option === 'studyYear'
                  ? 'studyYear'
                  : option === 'secondSemester'
                    ? 'second'
                    : 'first'
              )
        )
      )}
      {getMarkOptionLabel(option)}
    </Space>
  );

  // The colour of the public holidays, which are free days in a deeper red
  const holidayLegend = (
    <Space size={6}>
      <span
        style={{
          display: 'inline-block',
          width: 12,
          height: 12,
          borderRadius: token.borderRadiusSM,
          border: `1px solid ${token.colorBorder}`,
          verticalAlign: 'middle',
          ...getHolidayStyle(token),
        }}
      />
      {t('academicCalendar.publicHoliday')}
    </Space>
  );

  // A study year saved before the calendars has only one start / end, shown like the first semester's
  const markOptions =
    setupStep === 'studyYear'
      ? STUDY_YEAR_MARK_OPTIONS
      : showsSemesters
        ? MARK_OPTIONS
        : MARK_OPTIONS.filter(
            (option) => option !== 'secondSemester' && option !== 'studyYear'
          );

  if (isLoading || isLoadingStudyYears || isLoadingHolidays) {
    return <Spin />;
  }

  if (!isSetup && createdYearStarts.size === 0) {
    return (
      <Alert type="info" showIcon message={t('academicCalendar.noStudyYear')} />
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {chosenYearStart === undefined && (
        <Typography.Title level={3}>
          {t('homePage.academicCalendar')}
        </Typography.Title>
      )}

      {/* The study year being set up is fixed: the first one which isn't created yet */}
      <Space
        wrap
        style={{
          marginBottom: 16,
          display: chosenYearStart === undefined ? undefined : 'none',
        }}
      >
        <Typography.Text strong>
          {t('academicCalendar.studyYear')}:
        </Typography.Text>
        <Typography.Text>{`${studyYearStart}/${studyYearStart + 1}`}</Typography.Text>
        {studyYearStart === nextYearStart && (
          <Tag color="green">{t('academicCalendar.notCreated')}</Tag>
        )}
      </Space>

      {/* The four calendars as tabs, in the setup a dot marks the ones changed from the defaults.
          The start / end of the study year is the same in all four, so they aren't chosen in its step;
          a study year saved before the calendars has none to choose from */}
      {setupStep !== 'studyYear' &&
        (savedStudyYear === undefined ||
          savedStudyYear.calendars.length > 0) && (
          <div
            role="tablist"
            aria-label={t('academicCalendar.calendars')}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              marginBottom: 16,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            {CALENDAR_TABS.map((tab) => {
              const isSelected =
                tab.languages === calendarLanguages &&
                tab.yearType === yearType;
              const isModified = Object.values(
                changes[studyYearStart]?.[tab.languages]?.[tab.yearType] ?? {}
              ).some((semesterChanges) => semesterChanges !== undefined);
              return (
                <button
                  key={`${tab.languages}-${tab.yearType}`}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  onClick={() => {
                    setCalendarLanguages(tab.languages);
                    setYearType(tab.yearType);
                    cancelSelection();
                  }}
                  style={{
                    minHeight: 52,
                    padding: '8px 16px',
                    border: 0,
                    borderBottom: `3px solid ${isSelected ? token.colorPrimary : 'transparent'}`,
                    background: isSelected
                      ? token.colorPrimaryBg
                      : 'transparent',
                    borderRadius: `${token.borderRadius}px ${token.borderRadius}px 0 0`,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    font: 'inherit',
                    textAlign: 'left',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: isSelected
                          ? token.colorPrimary
                          : token.colorText,
                      }}
                    >
                      {tab.languages === CalendarLanguages.RomanianEnglish
                        ? t('academicCalendar.languagesRoEn')
                        : t('academicCalendar.languagesHuDe')}
                    </span>
                    <span
                      style={{ fontSize: 12, color: token.colorTextSecondary }}
                    >
                      {tab.yearType === CalendarYearType.Final
                        ? t('academicCalendar.yearTypeFinal')
                        : t('academicCalendar.yearTypeNonFinal')}
                    </span>
                  </span>
                  {isModified && (
                    <span
                      title={t('academicCalendar.calendarModified')}
                      aria-label={t('academicCalendar.calendarModified')}
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: token.colorWarning,
                        flex: 'none',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

      {/* A semester which doesn't fit into the study year, in both setup steps */}
      {isSetup && !isSaved && calendarWarnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t('academicCalendar.calendarInvalidTitle')}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {calendarWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Everything but the start / end of the study year is set per calendar,
          so a change in one tab doesn't show in the others */}
      {isSetup && setupStep !== 'studyYear' && (
        <Typography.Paragraph
          type="secondary"
          style={{ fontSize: 13, marginBottom: 16 }}
        >
          <InfoCircleOutlined style={{ marginRight: 6 }} />
          {t('academicCalendar.separateCalendarsNote')}
        </Typography.Paragraph>
      )}

      {isSetup ? (
        <>
          {/* What a click on a day does, also the legend of the colours */}
          <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
            {t('academicCalendar.markAs')}:
          </Typography.Text>
          {/* The options are separate chips which wrap on a small screen;
              none is shown selected while nothing is chosen */}
          <div
            role="radiogroup"
            aria-label={t('academicCalendar.markAs')}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: 8,
            }}
          >
            {markOptions.map((option) => {
              // only in the legend: the time outside of the teaching period
              // comes from the start / end of the semesters, it can't be marked;
              // the end of the study year is set in its own step
              const isLegendOnly =
                option === 'outside' ||
                (option === 'studyYear' && setupStep !== 'studyYear');
              if (isLegendOnly) {
                return (
                  <span key={option} style={legendOnlyStyle}>
                    {renderMarkOption(option)}
                  </span>
                );
              }
              const isSelected = markOption === option;
              return (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  disabled={isSaved}
                  onClick={() => {
                    setMarkOption(option);
                    cancelSelection();
                  }}
                  style={getMarkChipStyle(isSelected, !isSaved)}
                >
                  {renderMarkOption(option)}
                </button>
              );
            })}
            {/* only in the legend: the public holidays come from the calendar of Romania */}
            <span style={legendOnlyStyle}>{holidayLegend}</span>
          </div>
        </>
      ) : (
        // The study years page only shows the saved calendars, so the colours are just a legend
        <Space wrap size="large" style={{ marginBottom: 16 }}>
          {markOptions.map((option) => (
            <span key={option}>{renderMarkOption(option)}</span>
          ))}
          {holidayLegend}
          <Space size={6}>
            <span
              style={{
                display: 'inline-block',
                width: 12,
                height: 12,
                borderRadius: token.borderRadiusSM,
                verticalAlign: 'middle',
                ...getTodayStyle(token),
              }}
            />
            {t('academicCalendar.today')}
          </Space>
        </Space>
      )}

      {/* The calendar page only shows the saved study years, they are changed in the study year setup */}
      {isSetup && (
        <>
          {/* What the next click does; while the last day of an interval or of the period
              is awaited, it is shown in another colour so the second click isn't missed */}
          <Alert
            showIcon
            type={
              !isSaved &&
              (isSemesterBoundary(markOption) || isStudyYearBoundary(markOption)
                ? nextBoundary === 'end'
                : rangeStart)
                ? 'warning'
                : 'info'
            }
            message={
              <Typography.Text strong>
                {isSaved
                  ? t('academicCalendar.savedReadOnly')
                  : isStudyYearBoundary(markOption)
                    ? nextBoundary === 'start'
                      ? t('academicCalendar.studyYearStartClickHint')
                      : t('academicCalendar.studyYearEndClickHint')
                    : isSemesterBoundary(markOption)
                      ? nextBoundary === 'start' && !setsOnlyEnd
                        ? t('academicCalendar.periodClickHintStart')
                        : t('academicCalendar.periodClickHintEnd')
                      : isRangeStatus
                        ? rangeStart
                          ? t('academicCalendar.rangeClickHintEnd')
                          : t('academicCalendar.rangeClickHintStart')
                        : t('academicCalendar.clickHint')}
              </Typography.Text>
            }
            style={{ marginBottom: 8 }}
          />

          <div style={{ marginBottom: 16 }}>
            <Button onClick={resetShown} disabled={isSaved || !hasChanges}>
              {t('academicCalendar.resetToDefault')}
            </Button>
          </div>
        </>
      )}

      {isHolidaysError && (
        <Alert
          type="warning"
          showIcon
          message={t('academicCalendar.holidaysLoadError')}
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[24, 32]} onMouseLeave={() => setHoveredKey(undefined)}>
        {months.map(({ year, month }) => (
          // 4 months in a row on a wide screen, fewer on a smaller one
          <Col key={`${year}-${month}`} xs={24} md={12} lg={8} xl={6} xxl={4}>
            <MonthCalendar
              year={year}
              month={month}
              getDayStatus={getDayStatus}
              onDayClick={
                isSaved || !markOption
                  ? undefined
                  : isStudyYearBoundary(markOption)
                    ? setStudyYearBoundary
                    : isSemesterBoundary(markOption)
                      ? setBoundary
                      : isRangeStatus
                        ? markRange
                        : toggleDay
              }
              getHolidayName={getHolidayName}
              getPeriodBoundary={getPeriodBoundary}
              canClickDay={canClickDay}
              onDayHover={(date) => setHoveredKey(toDateKey(date))}
              isInRangePreview={isInRangePreview}
              isInStudyYear={isDayInStudyYear}
              isInSecondSemester={isDayInSecondSemester}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};
